import { disableBodyScroll, enableBodyScroll } from '@thiememeulenhoff/js-sdk';
import { html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { TmElement } from '../../custom-element.helper';

export class TmDialogElement extends TmElement {
  @query('dialog')
  dialog!: HTMLDialogElement;

  @query('slot[name="trigger"]')
  triggerSlot!: HTMLSlotElement;

  @query('#trigger')
  trigger!: HTMLButtonElement;

  @query('#content')
  content!: HTMLDivElement;

  @property({ type: Boolean, reflect: true })
  contained = false;

  @property({ type: Boolean, attribute: 'keep-alive' })
  keepAlive = false;

  @property({ type: Boolean, reflect: true })
  disabled?: boolean;

  @property({ type: Boolean, reflect: true })
  loading?: false;

  extraControls() {
    return html``;
  }

  #backdropClickEnabled = true;

  protected closeButton() {
    if (this.contained) {
      return this.title
        ? nothing
        : html`<div class="close-button-wrapper">
            <button
              part="close-button"
              class="close-contained"
              aria-label="close"
              @click=${this.close}
              @touchstart=${this.close}
            >
              <a-icon name="close"></a-icon>
            </button>
          </div>`;
    }
    return html`<button
      part="close-button"
      aria-label="close"
      class="close"
      @click=${this.close}
      @touchstart=${this.close}
    >
      <a-icon name="close"></a-icon>
    </button>`;
  }

  protected header() {
    return this.title
      ? html`<header>
          <div part="header">${unsafeHTML(this.title)}</div>
          ${this.contained
            ? html`<button
                part="close-button"
                @click=${this.close}
                @touchstart=${this.close}
              >
                <a-icon name="close"></a-icon>
              </button>`
            : nothing}
        </header>`
      : nothing;
  }

  protected dialogContent() {
    return html`<container part="container">
      ${this.header()}
      <main part="main" id="content"><slot></slot></main>
      ${this.extraControls()} ${this.closeButton()}
    </container>`;
  }

  protected loaderContent() {
    return this.loading
      ? html`
          <div class="loaderBackground"></div>
          <div class="loading" part="loader" role="loader">
            <a-loader></a-loader>
          </div>
        `
      : nothing;
  }

  protected modalTemplate() {
    return html`
      <dialog
        data-test="dialog"
        part="dialog"
        role="dialog"
        aria-label="dialog"
        @mousedown=${this.#handleBackDropClick}
        @touchstart=${this.#handleBackDropClick}
        tabindex="-1"
      >
        ${this.dialogContent()} ${this.loaderContent()}
      </dialog>
    `;
  }

  override render() {
    return html`
      <div
        class="trigger-container"
        @click=${this.open}
        @keydown=${this.#handleKeyDown}
      >
        <slot name="trigger"></slot>
      </div>
      ${this.modalTemplate()}
    `;
  }

  #handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.open();
    }
  }

  override async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    // Just open it up when there is no trigger
    // This can be handy to add a modal programmatically,
    // or if you need to open up the modal directly from a template.
    if (this.#noTrigger()) {
      this.open();
    }

    this.dialog.addEventListener('close', () => this.onClose());
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.dialog?.removeEventListener('close', this.onClose);

    if (this.dialog.open) {
      enableBodyScroll(this.content);
    }
  }

  open() {
    this.dispatchLocalEvent({
      eventName: 'open',
    });

    this.dialog.inert = true;
    this.dialog.showModal();
    this.dialog.inert = false;
    disableBodyScroll(this.content);
  }

  close(closeReason?: string) {
    if (!this.dialog.open || this.disabled) {
      return;
    }

    this.dialog.classList.add('hide');
    this.dialog.addEventListener('animationend', (event) => {
      if (
        event.animationName === 'fade-out' ||
        event.animationName.includes('slide-out')
      ) {
        enableBodyScroll(this.content);
        this.dialog.classList.remove('hide');
        this.dialog.close();

        if (closeReason) {
          this.dispatchGlobalEvent({
            eventName: closeReason,
          });
        }
      }
    });
  }

  onClose() {
    this.dispatchLocalEvent({
      eventName: 'close',
    });

    enableBodyScroll(this.content);

    if (this.#noTrigger() && !this.keepAlive) {
      this.remove();
    }
  }

  get backdropClickEnabled() {
    return this.#backdropClickEnabled;
  }

  set backdropClickEnabled(value: boolean) {
    this.#backdropClickEnabled = value;
  }

  #noTrigger() {
    const assignedElements = this.triggerSlot?.assignedElements({
      flatten: true,
    });
    return assignedElements?.length === 0;
  }

  #handleBackDropClick(event: MouseEvent) {
    if (this.#clickIsOnBackdrop(event) && this.#backdropClickEnabled) {
      this.close();
    }
  }

  #clickIsOnBackdrop(event: MouseEvent) {
    // Because we have an inner container ".content" we can say with
    // confidence that a click on `this.modal` is a click on the backdrop.
    return event.target === this.dialog;
  }
}
